import { RegistrantEmailAssignment } from "@/components/non_registrant/registrant-email-assignment";
import { UnregisteredRegistrationQuestions } from "@/components/non_registrant/unregistered-registration-questions";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import {
  createUnregisteredPropertyMutation,
  getNonRegistrantRegistrantAccountsOptions,
  getPartyOptions,
  getQuestionOptionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { ForeclosureLicenseTypeOption } from "@/hera-api/foreclosure-license-link-api";
import { useForeclosurePartyQuestionIds } from "@/hooks/use-foreclosure-party-question-ids";
import {
  type PartyContactOption,
  answersDictFromLicenseAnswers,
  buildUnregisteredInitialAnswers,
  foreclosureCaseSuggestionWriteBack,
  partyContactOptionFor,
  partyIdFromUnregisteredAnswer,
} from "@/lib/unregistered-registration-answers";
import { getLocalTodayYmd, getQueryErrorMessage } from "@/lib/utils";
import type { LicenseAnswers } from "@/types/license";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import { useEffect, useMemo, useState } from "react";

export type ForeclosureUnregisteredCreatedOverrides = {
  overrideOmtPartyId?: string | null;
  overrideAmsPartyId?: string | null;
  overrideRegistrantAccountId?: string | null;
};

export interface CreateUnregisteredFromForeclosureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jurisdictionId: string;
  licenseTypes: ForeclosureLicenseTypeOption[];
  caseNumber: string;
  address1: string;
  city: string;
  state: string;
  zipcode: string;
  apn: string;
  latitude: number | null;
  longitude: number | null;
  defaultEffectiveDate?: string | null;
  defaultOwnerName?: string | null;
  defaultRegistrantAccountId?: string | null;
  defaultOmtPartyId?: string | null;
  defaultAmsPartyId?: string | null;
  portal?: "government" | "operator";
  onCreated: (overrides?: ForeclosureUnregisteredCreatedOverrides) => void;
}

export function CreateUnregisteredFromForeclosureDialog({
  open,
  onOpenChange,
  jurisdictionId,
  licenseTypes,
  caseNumber,
  address1: initialAddress1,
  city: initialCity,
  state: initialState,
  zipcode: initialZip,
  apn: initialApn,
  latitude,
  longitude,
  defaultEffectiveDate,
  defaultOwnerName,
  defaultRegistrantAccountId,
  defaultOmtPartyId,
  defaultAmsPartyId,
  portal = "operator",
  onCreated,
}: CreateUnregisteredFromForeclosureDialogProps) {
  const [licenseTypeId, setLicenseTypeId] = useState("");
  const [address1, setAddress1] = useState(initialAddress1);
  const [city, setCity] = useState(initialCity);
  const [state, setState] = useState(initialState);
  const [zipcode, setZipcode] = useState(initialZip);
  const [apn, setApn] = useState(initialApn);
  const [effectiveDate, setEffectiveDate] = useState(
    defaultEffectiveDate || getLocalTodayYmd(),
  );
  const [registrantAccountId, setRegistrantAccountId] = useState<string | null>(
    null,
  );
  const [registrantEmail, setRegistrantEmail] = useState("");
  const [registrantEmailBlocked, setRegistrantEmailBlocked] = useState(false);
  const [ownerName, setOwnerName] = useState(defaultOwnerName ?? "");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [questionAnswers, setQuestionAnswers] = useState<
    Partial<LicenseAnswers>
  >({});

  const { omtQuestionId, amsQuestionId, caseNumberQuestionId, questions } =
    useForeclosurePartyQuestionIds(jurisdictionId, portal);

  useEffect(() => {
    if (!open) return;
    setLicenseTypeId(licenseTypes[0]?.license_type_id ?? "");
    setAddress1(initialAddress1);
    setCity(initialCity);
    setState(initialState);
    setZipcode(initialZip);
    setApn(initialApn);
    setEffectiveDate(defaultEffectiveDate || getLocalTodayYmd());
    setRegistrantAccountId(defaultRegistrantAccountId ?? null);
    setRegistrantEmail("");
    setRegistrantEmailBlocked(false);
    setOwnerName(defaultOwnerName ?? "");
    setOwnerEmail("");
    setOwnerPhone("");
  }, [
    open,
    licenseTypes,
    initialAddress1,
    initialCity,
    initialState,
    initialZip,
    initialApn,
    defaultEffectiveDate,
    defaultOwnerName,
    defaultRegistrantAccountId,
  ]);

  const omtOptionsQuery = useQuery({
    ...getQuestionOptionsOptions({
      query: {
        question_id: omtQuestionId ?? undefined,
        include_deleted: false,
      },
    }),
    enabled: open && Boolean(omtQuestionId),
  });

  const amsOptionsQuery = useQuery({
    ...getQuestionOptionsOptions({
      query: {
        question_id: amsQuestionId ?? undefined,
        include_deleted: false,
      },
    }),
    enabled: open && Boolean(amsQuestionId),
  });

  const omtOptions = useMemo(
    () =>
      (omtOptionsQuery.data?.question_options ?? []) as PartyContactOption[],
    [omtOptionsQuery.data?.question_options],
  );
  const amsOptions = useMemo(
    () =>
      (amsOptionsQuery.data?.question_options ?? []) as PartyContactOption[],
    [amsOptionsQuery.data?.question_options],
  );

  // Prefer the case AMS; when missing, reuse OMT so create-from-review still
  // prefills AMS contact fields.
  const amsPartyIdForPrefill = defaultAmsPartyId ?? defaultOmtPartyId ?? null;

  // The suggested party is not always configured as an option on the question,
  // so fall back to the party record itself to keep the prefill working.
  const omtPartyQuery = useQuery({
    ...getPartyOptions({ path: { party_id: defaultOmtPartyId ?? "" } }),
    enabled: open && Boolean(defaultOmtPartyId),
  });

  const amsPartyQuery = useQuery({
    ...getPartyOptions({ path: { party_id: amsPartyIdForPrefill ?? "" } }),
    enabled: open && Boolean(amsPartyIdForPrefill),
  });

  const omtOption = useMemo(
    () =>
      partyContactOptionFor(defaultOmtPartyId, omtOptions, omtPartyQuery.data),
    [defaultOmtPartyId, omtOptions, omtPartyQuery.data],
  );
  const amsOption = useMemo(
    () =>
      partyContactOptionFor(
        amsPartyIdForPrefill,
        amsOptions,
        amsPartyQuery.data,
      ) ?? omtOption,
    [amsOptions, amsPartyIdForPrefill, amsPartyQuery.data, omtOption],
  );

  const answersSeed = useMemo(
    () =>
      buildUnregisteredInitialAnswers({
        omtQuestionId,
        amsQuestionId,
        caseNumberQuestionId,
        omtOption,
        amsOption,
        caseNumber,
        questions,
      }),
    [
      amsOption,
      amsQuestionId,
      caseNumber,
      caseNumberQuestionId,
      omtOption,
      omtQuestionId,
      questions,
    ],
  );

  useEffect(() => {
    if (!open) return;
    setQuestionAnswers(answersSeed);
  }, [answersSeed, open]);

  // All primary accounts, not only those with a license in this jurisdiction —
  // operators often link a registrant that has not registered here yet.
  const registrantsQuery = useQuery({
    ...getNonRegistrantRegistrantAccountsOptions({
      query: { include_deleted: false, primaries_only: true },
    }),
    enabled: open && !!jurisdictionId,
  });

  const selectedRegistrant = useMemo(
    () =>
      registrantsQuery.data?.registrant_accounts.find(
        (account) => account.id === registrantAccountId,
      ) ?? null,
    [registrantAccountId, registrantsQuery.data?.registrant_accounts],
  );

  const licenseTypeOptions = useMemo(
    () =>
      licenseTypes.map((item) => ({
        value: item.license_type_id,
        label: item.name,
      })),
    [licenseTypes],
  );

  const selectedLicenseType = useMemo(() => {
    const match = licenseTypes.find(
      (item) => item.license_type_id === licenseTypeId,
    );
    return match ? { id: match.license_type_id, name: match.name } : null;
  }, [licenseTypeId, licenseTypes]);

  const createMutation = useMutation({
    ...createUnregisteredPropertyMutation(),
  });

  const canSubmit =
    !!licenseTypeId &&
    address1.trim().length > 0 &&
    city.trim().length > 0 &&
    state.trim().length > 0 &&
    zipcode.trim().length > 0 &&
    !!effectiveDate &&
    !registrantEmailBlocked &&
    !createMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const selectedOmtPartyId = omtQuestionId
      ? partyIdFromUnregisteredAnswer(
          questionAnswers[omtQuestionId],
          omtOptions,
        )
      : null;
    const selectedAmsPartyId = amsQuestionId
      ? partyIdFromUnregisteredAnswer(
          questionAnswers[amsQuestionId],
          amsOptions,
        )
      : null;
    try {
      const created = await createMutation.mutateAsync({
        body: {
          jurisdiction_id: jurisdictionId,
          license_type_id: licenseTypeId,
          property_id: null,
          property: {
            jurisdiction_id: jurisdictionId,
            address1: address1.trim(),
            address2: null,
            city: city.trim(),
            state: state.trim(),
            zipcode: zipcode.trim(),
            latitude: latitude ?? 0,
            longitude: longitude ?? 0,
            apn: apn.trim() || null,
          },
          effective_date: effectiveDate,
          owner_name: ownerName.trim() || null,
          owner_address1: null,
          owner_address2: null,
          owner_city: null,
          owner_state: null,
          owner_zip: null,
          owner_email: ownerEmail.trim() || null,
          owner_phone: ownerPhone.trim() || null,
          registrant_account_id: registrantEmail.trim()
            ? null
            : registrantAccountId,
          registrant_email: registrantEmail.trim() || null,
          answers_to_additional_questions:
            answersDictFromLicenseAnswers(questionAnswers),
        },
      });
      toast({
        title: "Unregistered property created",
        description: `Created for foreclosure case ${caseNumber}.`,
        variant: "secondary",
      });
      onCreated(
        foreclosureCaseSuggestionWriteBack({
          selectedOmtPartyId,
          selectedAmsPartyId,
          selectedRegistrantAccountId:
            created.registrant_account_id ?? registrantAccountId,
          currentOmtPartyId: defaultOmtPartyId ?? null,
          currentAmsPartyId: defaultAmsPartyId ?? null,
          currentRegistrantAccountId: defaultRegistrantAccountId ?? null,
        }),
      );
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Failed to create unregistered property",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-5xl [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle>Create unregistered property</DialogTitle>
          <DialogDescription>
            Prefills from ATTOM foreclosure case {caseNumber}. Confirm details
            and optionally link a registrant.
          </DialogDescription>
        </DialogHeader>

        <div className="grid min-w-0 gap-4 py-2">
          <div className="grid gap-2">
            <Label>License type</Label>
            <Select
              options={licenseTypeOptions}
              value={licenseTypeId ? [licenseTypeId] : []}
              onValueChange={(value) => setLicenseTypeId(value[0] ?? "")}
              multiple={false}
              placeholder="Select foreclosure license type"
              emptyMessage="No foreclosure license type configured."
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="fc-unreg-address1">Street address</Label>
            <Input
              id="fc-unreg-address1"
              value={address1}
              onChange={(event) => setAddress1(event.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-2 sm:col-span-1">
              <Label htmlFor="fc-unreg-city">City</Label>
              <Input
                id="fc-unreg-city"
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fc-unreg-state">State</Label>
              <Input
                id="fc-unreg-state"
                value={state}
                onChange={(event) => setState(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fc-unreg-zip">ZIP</Label>
              <Input
                id="fc-unreg-zip"
                value={zipcode}
                onChange={(event) => setZipcode(event.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="fc-unreg-apn">APN</Label>
            <Input
              id="fc-unreg-apn"
              value={apn}
              onChange={(event) => setApn(event.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>Effective date</Label>
            <DatePicker
              value={
                effectiveDate
                  ? parse(effectiveDate, "yyyy-MM-dd", new Date())
                  : null
              }
              onChange={(date) => {
                setEffectiveDate(date ? format(date, "yyyy-MM-dd") : "");
              }}
            />
          </div>

          <RegistrantEmailAssignment
            registrantAccountId={registrantAccountId}
            onRegistrantAccountIdChange={setRegistrantAccountId}
            email={registrantEmail}
            onEmailChange={setRegistrantEmail}
            enabled={open}
            onBlockedChange={setRegistrantEmailBlocked}
          />

          {selectedLicenseType ? (
            <UnregisteredRegistrationQuestions
              licenseType={selectedLicenseType}
              jurisdictionId={jurisdictionId}
              initialAnswers={answersSeed}
              onAnswersChange={setQuestionAnswers}
              userType={
                portal === "government"
                  ? UserType.GOVERNMENT
                  : UserType.OPERATOR
              }
              registrantAccount={selectedRegistrant}
            />
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="fc-unreg-owner-name">Owner name</Label>
              <Input
                id="fc-unreg-owner-name"
                value={ownerName}
                onChange={(event) => setOwnerName(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fc-unreg-owner-email">Owner email</Label>
              <Input
                id="fc-unreg-owner-email"
                value={ownerEmail}
                onChange={(event) => setOwnerEmail(event.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="fc-unreg-owner-phone">Owner phone</Label>
              <Input
                id="fc-unreg-owner-phone"
                value={ownerPhone}
                onChange={(event) => setOwnerPhone(event.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {createMutation.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
